import axios from 'axios'
import type { AxiosInstance } from 'axios'
import { logger } from '@sidd/chatbot-core'
import type { IAnalyticsCallback, IAnalyticsEvent } from '@sidd/chatbot-core'
import { detectBrowser, type IBrowserInfo } from '../utils/browser-info'

interface IDataLakeAnalyticsEntry {
  readonly id: string
  readonly event_type: string
  readonly timestamp: string
  readonly source: string
  readonly deployment_type: string
  readonly event_name: string
  readonly session_id?: string
  readonly user_id?: string
  readonly chatbot_id?: string
  readonly tenant?: string
  readonly browser: IBrowserInfo
  readonly [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | string[]
    | IBrowserInfo
    | Record<string, string | number | boolean | null | undefined | string[]>
}

type AuthCallback = () => Promise<{
  token: string
  tenantId: string
  userId?: string
}>

const COMPONENT = 'useDataLakeAnalytics'

function getAnalyticsBaseUrl(): string {
  return import.meta.env.VITE_ANALYTICS_BASE_URL || ''
}

function buildEntry(
  event: IAnalyticsEvent,
  browser: IBrowserInfo
): IDataLakeAnalyticsEntry {
  return {
    id: crypto.randomUUID(),
    event_type: event.eventName,
    timestamp: event.timestamp,
    source: event.source || 'chatbot',
    deployment_type: 'WebDirect',
    event_name: event.eventName,
    session_id: event.sessionId,
    user_id: event.userId,
    chatbot_id: event.chatbotId,
    tenant: event.tenant,
    browser,
    ...event.payload,
  }
}

export function useDataLakeAnalytics(getAuth: AuthCallback) {
  const baseUrl = getAnalyticsBaseUrl()
  const browser = detectBrowser()
  let client: AxiosInstance | null = null
  let pendingEntry: IDataLakeAnalyticsEntry | null = null

  if (!baseUrl) {
    logger.warn(
      'VITE_ANALYTICS_BASE_URL not configured — analytics disabled',
      undefined,
      { component: COMPONENT }
    )
  }

  function getClient(): AxiosInstance {
    if (!client) {
      client = axios.create({
        baseURL: baseUrl,
        timeout: 10_000,
        headers: { 'Content-Type': 'application/json' },
      })
    }
    return client
  }

  async function sendEntry(entry: IDataLakeAnalyticsEntry): Promise<void> {
    try {
      const auth = await getAuth()
      await getClient().post('/v1/Analytics/Studio/', entry, {
        headers: { Authorization: `Bearer ${auth.token}` },
      })
    } catch (error) {
      logger.warn(
        'Failed to send analytics event (non-blocking)',
        {
          eventName: entry.event_name,
          error: error instanceof Error ? error.message : String(error),
        },
        { component: COMPONENT }
      )
    }
  }

  function flushBeacon(): void {
    if (!pendingEntry || !baseUrl) return

    const url = `${baseUrl}/v1/Analytics/Studio/`
    const blob = new Blob([JSON.stringify(pendingEntry)], {
      type: 'application/json',
    })
    navigator.sendBeacon(url, blob)
    pendingEntry = null
  }

  if (baseUrl && typeof window !== 'undefined') {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushBeacon()
    })
    window.addEventListener('beforeunload', flushBeacon)
  }

  function createAnalyticsCallback(): IAnalyticsCallback {
    return (event: IAnalyticsEvent): void => {
      if (!baseUrl) return

      const entry = buildEntry(event, browser)
      pendingEntry = entry
      sendEntry(entry).then(() => {
        if (pendingEntry === entry) pendingEntry = null
      })
    }
  }

  return { createAnalyticsCallback }
}
